// src/components/marketing/DreamCampus.tsx
'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { GraduationCap, ArrowRight, Sparkles, MapPin, Award, CheckCircle } from 'lucide-react'

interface Campus {
  id: string
  name: string
  location: string
  targetExam: string
  rankGoal: string
  tagline: string
  image: string
  gradient: string
  examLink: string
  highlights: string[]
}

const campuses: Campus[] = [
  {
    id: 'iit-bombay',
    name: 'IIT Bombay',
    location: 'Powai, Mumbai',
    targetExam: 'JEE Advanced',
    rankGoal: 'Top 500 AIR Target',
    tagline: "India's pinnacle of engineering & innovation",
    image: '/images/home/iit-bombay.jpg',
    gradient: 'from-blue-600 to-indigo-700',
    examLink: '/practice-exams?exam=jee-advanced',
    highlights: ['Multi-concept physics & math', 'Exact IIT-level mock papers', 'Rank calibration vs top aspirants']
  },
  {
    id: 'aiims-delhi',
    name: 'AIIMS New Delhi',
    location: 'Ansari Nagar, New Delhi',
    targetExam: 'NEET UG',
    rankGoal: '700+ Score Target',
    tagline: "The gold standard of medical education in India",
    image: '/images/home/aiims-delhi.jpg',
    gradient: 'from-emerald-600 to-teal-700',
    examLink: '/practice-exams?exam=neet',
    highlights: ['100% NCERT Biology coverage', 'Assertion-Reasoning focus', 'Strict 3h 20m speed practice']
  },
  {
    id: 'iit-delhi',
    name: 'IIT Delhi',
    location: 'Hauz Khas, New Delhi',
    targetExam: 'JEE Main & Advanced',
    rankGoal: '99.8+ Percentile Target',
    tagline: "World-class research, technology & startup ecosystem",
    image: '/images/home/iit-delhi.jpg',
    gradient: 'from-purple-600 to-indigo-700',
    examLink: '/practice-exams?exam=jee',
    highlights: ['Full syllabus test simulations', 'Chapter-wise speed tests', 'Detailed error analysis']
  },
  {
    id: 'iit-roorkee',
    name: 'IIT Roorkee & Top NITs',
    location: 'Roorkee, Uttarakhand',
    targetExam: 'JEE Main',
    rankGoal: '99+ Percentile Goal',
    tagline: "Prestigious engineering legacy & top campus placements",
    image: '/images/home/iit-roorkee.jpg',
    gradient: 'from-amber-600 to-orange-700',
    examLink: '/practice-exams?exam=jee-main',
    highlights: ['2026 NTA pattern compliance', 'Topic-wise mock drills', 'Real-time all India percentile']
  }
]

export function DreamCampus() {
  return (
    <section className="py-20 lg:py-28 relative overflow-hidden bg-slate-50/70 dark:bg-gray-900/50">
      {/* Decorative ambient background */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm font-semibold shadow-sm">
            <GraduationCap className="w-4 h-4" />
            <span>Target Your Dream Institute</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            Practice for the Campus You{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
              Aspire to Enter
            </span>
          </h2>

          <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
            Mockzy tests are rigorously benchmarked against the real cutoffs, question depth, and speed required for India's top colleges.
          </p>
        </div>

        {/* Campuses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {campuses.map((campus) => (
            <div
              key={campus.id}
              className="group relative rounded-3xl overflow-hidden bg-white dark:bg-gray-800/90 border border-gray-200/80 dark:border-gray-700/80 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 flex flex-col"
            >
              {/* Campus Photo with Overlay */}
              <div className="relative h-60 sm:h-72 w-full overflow-hidden">
                <Image
                  src={campus.image}
                  alt={`${campus.name} campus building`}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent opacity-85 group-hover:opacity-80 transition-opacity" />

                {/* Badges on top of image */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${campus.gradient} shadow-md`}>
                    {campus.targetExam}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 dark:bg-gray-900/90 backdrop-blur-md text-gray-900 dark:text-white border border-white/20 shadow-sm flex items-center gap-1">
                    <Award className="w-3.5 h-3.5 text-amber-500" />
                    {campus.rankGoal}
                  </span>
                </div>

                {/* College Title over image bottom */}
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="flex items-center gap-1.5 text-xs text-gray-300 font-medium mb-1">
                    <MapPin className="w-3.5 h-3.5 text-red-400" />
                    {campus.location}
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight text-white group-hover:text-blue-200 transition-colors">
                    {campus.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-200 line-clamp-1 mt-0.5 opacity-90">
                    {campus.tagline}
                  </p>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 sm:p-7 flex flex-col justify-between flex-1 space-y-6">
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Mockzy Prep Highlights
                  </p>
                  <ul className="space-y-2.5">
                    {campus.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300 font-medium">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action CTA */}
                <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    NTA 2026 Test Series Ready
                  </span>
                  <Link
                    href={campus.examLink}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-bold shadow hover:shadow-md transition-all duration-300 group/btn hover:scale-105"
                  >
                    <span>Practice Now</span>
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
